import { useCallback, useEffect, useRef, useState } from 'react'
import { useResendAfterAccess } from '@/hooks/useResendAfterAccess'
import { createAgentSession, getAgentSessionState } from '@/api/claudeagent'
import { WS_BASE } from '@/config/basePath'
import api from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import {
  loadAgentChat,
  saveAgentChat,
  clearAgentChat,
  type PersistedAgentMessage,
} from '@/lib/agentChatPersistence'
import { refreshCredits } from '@/store/creditsStore'
import { DOCK_PING_MS, afterSeqFor, unwindRefusedTurn } from '@/lib/dockSeq'
import { answerOpenAsks } from '@/lib/askRecords'
import { formatSpend } from '@/store/creditsStore'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface ChangedFile {
  path: string
  content: string
  /**
   * True when the run DELETED this file rather than writing it (`content` is
   * then always ''). Consumers must branch on it: a deleted model has to be
   * EVICTED from the UI, not reloaded — reloading it 404s and the surface
   * keeps showing the stale object. See the deletion note in
   * `ClaudeCodeService.get_changed_files`.
   */
  deleted?: boolean
}

export interface PermissionRequest {
  permission_id: string
  tool_name: string
  detail: string
  tool_input: Record<string, unknown>
}

/** A structured clarifying question the agent asked (with suggested answers). */
export interface AgentAsk {
  question: string
  options: string[]
  /** A recognised special case, each one because the ANSWER IS A SECRET and
   *  the bubble must collect it through a form instead of the chat:
   *  "connection" (a data source), "ioflow_connect" (an IOFlow application's
   *  credentials), "ioflow_publish" (an instance-admin authorization code). */
  kind?: 'connection' | 'ioflow_connect' | 'ioflow_publish'
  /** ioflow_connect: which application's credential fields to render. */
  app_id?: string
  /** ioflow_publish: the seed application awaiting authorization. */
  seed_json?: unknown
}

export interface AgentMessage {
  role: 'user' | 'assistant' | 'system' | 'tool' | 'permission' | 'ask' | 'activity'
  content: string
  timestamp: Date
  tool?: string
  file?: string
  permissionRequest?: PermissionRequest
  permissionDecision?: 'allow' | 'deny'
  /** The permission prompt went unanswered until the agent had to move on
   *  (the step was skipped, as if denied). */
  permissionExpired?: boolean
  /** role === 'ask': the structured question + clickable options. */
  ask?: AgentAsk
  /** role === 'ask': set once the user has answered, with their answer. */
  askAnswered?: boolean
  askAnsweredWith?: string
  /** role === 'activity': 'info' | 'warning' — warning renders amber. */
  activityLevel?: string
  /** role === 'activity': per-activity accounting (time/tokens/cost). */
  meta?: { elapsedS?: number; tokens?: number; costUsd?: number }
}

export interface AgentProgress {
  status: string
  elapsed: number
}

/** A single item from the agent's TodoWrite plan/checklist. */
export interface ClaudeTodo {
  content: string
  status: 'pending' | 'in_progress' | 'completed'
  activeForm?: string
}

interface WebSocketEvent {
  type: string
  content?: string
  message?: string
  tool?: string
  file?: string
  changed_files?: ChangedFile[]
  claude_session_id?: string
  status?: string
  elapsed?: number
  permission_id?: string
  tool_name?: string
  detail?: string
  tool_input?: Record<string, unknown>
  todos?: ClaudeTodo[]
  /** agent_status: CLI-level status (API throttled, retrying, compacting…) */
  subtype?: string
  /** run_state: whether a run is still active server-side (reconnect sync) */
  active?: boolean
  /** ask_request: a structured clarifying question + suggested answers. */
  question?: string
  options?: string[]
  kind?: 'connection' | 'ioflow_connect' | 'ioflow_publish'
  app_id?: string
  seed_json?: unknown
  /** agent_config: the backend's resolved per-task model/effort for a run. */
  task_type?: string
  model?: string
  effort?: string
  config_name?: string
  /** complete: the model(s) the CLI actually reported using this turn. */
  actual_model?: string
  /** budget_state: the turn hit its cost cap (auto-continue or paused). */
  exceeded?: boolean
  turn_cost_usd?: number
  build_cost_usd?: number
  auto_continue?: boolean
  attempt?: number
  /** ai_access_error: platform allowance exhausted / no usable key. */
  code?: string
  plan?: string | null
  plan_label?: string | null
  used?: number | null
  limit?: number | null
  /** activity: persistent granular progress line + optional accounting. */
  level?: string
  elapsed_s?: number
  tokens?: number
  cost_usd?: number
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Engine tag used to key the persisted dock conversation in localStorage. */
const PERSIST_ENGINE = 'claude'

function toPersistedMessage(m: AgentMessage): PersistedAgentMessage {
  return {
    ...m,
    timestamp:
      m.timestamp instanceof Date ? m.timestamp.toISOString() : String(m.timestamp),
  }
}

interface UseClaudeAgentOptions {
  project?: string
  onComplete?: (changedFiles: ChangedFile[]) => void
}

export function useClaudeAgent(options: UseClaudeAgentOptions = {}) {
  const { project = 'Draft Application', onComplete } = options
  const { user } = useAuth()
  const userId = user?.user_id || ''

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [wsStatus, setWsStatus] = useState<ConnectionStatus>('disconnected')
  const [error, setError] = useState<string | null>(null)
  const [agentProgress, setAgentProgress] = useState<AgentProgress | null>(null)
  const [changedFiles, setChangedFiles] = useState<ChangedFile[]>([])
  const [todos, setTodos] = useState<ClaudeTodo[]>([])

  const wsRef = useRef<WebSocket | null>(null)
  const streamAccRef = useRef('')
  // The last turn sent, so an out-of-credits refusal can be carried on once
  // the user fixes it (see useResendAfterAccess). `projectRef` keeps the
  // socket handler (memoised without `project`) pointed at the right project.
  const lastTaskRef = useRef<{ taskType: string; taskData: Record<string, unknown> } | null>(null)
  const projectRef = useRef(project)
  projectRef.current = project
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const fatalErrorRef = useRef(false)
  // One login refresh per drop: agent sockets are authenticated at the handshake,
  // so a reconnect after the access cookie lapsed is closed with 4401.
  const authRetryRef = useRef(false)
  // Multi-device: this tab's id on the shared session (its own echoes are
  // skipped) and the highest event seq applied (replays are de-duplicated and
  // a reconnect asks only for what it missed).
  const clientIdRef = useRef<string>(
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  )
  const lastSeqRef = useRef(0)
  // The session `lastSeqRef` was counted on (see lib/dockSeq).
  const connectedSidRef = useRef<string | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  // Conversation persistence: `restoredKeyRef` records which user+project the
  // persisted transcript was restored for (writes are suppressed until then so
  // a fresh mount can't clobber a stored conversation with empty state).
  // `stateRef` mirrors the persisted slice for throttled/final flushes.
  const restoredKeyRef = useRef<string | null>(null)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stateRef = useRef({ messages, todos, sessionId })
  stateRef.current = { messages, todos, sessionId }

  // Mirror of isStreaming for synchronous reads inside WS handlers (state
  // updates are async, so bursts of replayed events would read stale state).
  const isStreamingRef = useRef(false)
  const setStreaming = useCallback((v: boolean) => {
    isStreamingRef.current = v
    setIsStreaming(v)
  }, [])

  // ---------------------------------------------------------------
  // WebSocket message handler
  // ---------------------------------------------------------------
  const handleMessage = useCallback((msg: WebSocketEvent) => {
    switch (msg.type) {
      case 'assistant_text':
        if (msg.content) {
          streamAccRef.current += msg.content
          setMessages((prev) => {
            const updated = [...prev]
            const last = updated[updated.length - 1]
            if (last && last.role === 'assistant') {
              updated[updated.length - 1] = {
                ...last,
                content: streamAccRef.current,
              }
            } else {
              updated.push({
                role: 'assistant',
                content: streamAccRef.current,
                timestamp: new Date(),
              })
            }
            return updated
          })
        }
        break

      case 'tool_activity':
        if (msg.tool) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'tool',
              content: `${msg.tool}${msg.file ? `: ${msg.file}` : ''}`,
              timestamp: new Date(),
              tool: msg.tool,
              file: msg.file,
            },
          ])
          // Reset accumulator so next assistant text creates a new message
          streamAccRef.current = ''
          // `tool` arrives already humanized ("Reviewing the Task Dashboard",
          // "Reports & dashboards designer") — keep its casing. Panels that map
          // legacy raw tool names to labels lower-case their own lookup key.
          setAgentProgress((prev) =>
            prev ? { ...prev, status: msg.tool! } : prev
          )
        }
        break

      case 'activity':
        // Persistent granular progress line (connection established, tables
        // found, which table is being examined, per-turn time/token/cost…) —
        // stays in the chat history, unlike transient tool/status chatter.
        if (msg.message) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'activity',
              content: msg.message!,
              timestamp: new Date(),
              activityLevel: msg.level || 'info',
              meta:
                msg.elapsed_s != null || msg.tokens != null || msg.cost_usd != null
                  ? { elapsedS: msg.elapsed_s, tokens: msg.tokens, costUsd: msg.cost_usd }
                  : undefined,
            },
          ])
          // Next assistant text starts a fresh bubble after an activity line.
          streamAccRef.current = ''
        }
        break

      case 'todos':
        // The agent's plan/checklist — replace wholesale with the latest
        // snapshot (TodoWrite always sends the full list).
        setTodos(Array.isArray(msg.todos) ? msg.todos : [])
        break

      case 'progress':
        setAgentProgress({
          status: msg.status || 'thinking',
          elapsed: msg.elapsed || 0,
        })
        break

      case 'agent_status':
        // CLI-level status (API throttled / retrying / compacting…) — shown
        // in the progress line so the agent doesn't look frozen.
        if (msg.message || msg.subtype) {
          setAgentProgress((prev) => ({
            status: msg.message || msg.subtype || 'working',
            elapsed: prev?.elapsed ?? 0,
          }))
        }
        break

      case 'budget_state':
        // The turn hit its cost cap mid-work. When the backend is
        // auto-continuing, this is informational; when it is NOT (limits
        // reached), tell the user the plan is paused and resumable — the
        // work done so far is real, not an error.
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            // The budget is configured in dollars but reported to the user in
            // credits, like every other spend figure they see.
            content: msg.auto_continue
              ? `⏳ Turn budget reached (${formatSpend(msg.build_cost_usd) ?? '0 credits'} used so far) — continuing automatically (attempt ${msg.attempt}).`
              : `⏸ Build budget limit reached (${formatSpend(msg.build_cost_usd) ?? '0 credits'} used). Progress is saved and the plan is resumable — send "continue" to keep going.`,
            timestamp: new Date(),
          },
        ])
        break

      case 'agent_config':
        // The backend's RESOLVED per-task model/effort for this run — lets
        // the header ModelChip's model/thinking dot show what actually runs
        // (incl. legacy-matrix and platform-default resolution the client
        // can't compute from the bundle alone).
        window.dispatchEvent(
          new CustomEvent('twasta:agent-config', {
            detail: {
              task_type: msg.task_type || '',
              model: msg.model || '',
              effort: msg.effort || '',
              config_name: msg.config_name || '',
            },
          }),
        )
        break

      case 'run_state': {
        // Reconnect sync: the backend reports whether a run is still active.
        // Also sent when a run ends, so a UI that missed the terminal event
        // (disconnected at the time) can clear its "working…" state.
        const active = !!msg.active
        if (active) {
          setStreaming(true)
        } else if (isStreamingRef.current) {
          setStreaming(false)
          setAgentProgress(null)
          setMessages((prev) => [
            ...prev,
            { role: 'system', content: '[Agent run ended]', timestamp: new Date() },
          ])
        }
        break
      }

      case 'complete':
        // An agent turn is the moment credits actually move, so pull the new
        // balance rather than waiting up to 60s for the menu's poll.
        refreshCredits()
        setStreaming(false)
        setAgentProgress(null)
        // The model(s) the CLI actually used this turn — let the header chip
        // reconcile "what I configured" vs "what really ran" (the CLI may add
        // its own internal helper model alongside the configured one).
        if (msg.actual_model) {
          window.dispatchEvent(
            new CustomEvent('twasta:agent-actual-model', {
              detail: {
                task_type: msg.task_type || '',
                actual_model: msg.actual_model,
              },
            }),
          )
        }
        if (msg.changed_files && msg.changed_files.length > 0) {
          setChangedFiles(msg.changed_files)
          setMessages((prev) => [
            ...prev,
            {
              role: 'system',
              content: `Updated ${msg.changed_files!.length} file${msg.changed_files!.length !== 1 ? 's' : ''}: ${msg.changed_files!.map((f) => f.path).join(', ')}`,
              timestamp: new Date(),
            },
          ])
          onCompleteRef.current?.(msg.changed_files)
        } else {
          onCompleteRef.current?.([])
        }
        break

      case 'cancelled':
        setStreaming(false)
        setAgentProgress(null)
        if (msg.changed_files && msg.changed_files.length > 0) {
          setChangedFiles(msg.changed_files)
        }
        setMessages((prev) => [
          ...prev,
          { role: 'system', content: '[Task cancelled by user]', timestamp: new Date() },
        ])
        break

      case 'permission_request':
        if (msg.permission_id && msg.tool_name) {
          const permReq: PermissionRequest = {
            permission_id: msg.permission_id,
            tool_name: msg.tool_name,
            detail: msg.detail || '',
            tool_input: msg.tool_input || {},
          }
          setMessages((prev) => {
            // Reconnect sync re-sends a still-pending prompt; if the restored
            // transcript already carries it, don't add a duplicate bubble.
            if (prev.some(
              (m) => m.role === 'permission'
                && m.permissionRequest?.permission_id === msg.permission_id,
            )) {
              return prev
            }
            return [
              ...prev,
              {
                role: 'permission',
                content: `${msg.tool_name}: ${msg.detail || ''}`,
                timestamp: new Date(),
                tool: msg.tool_name,
                permissionRequest: permReq,
              },
            ]
          })
          // Reset accumulator so next assistant text creates a new message
          streamAccRef.current = ''
          setAgentProgress((prev) =>
            prev ? { ...prev, status: 'awaiting_permission' } : prev
          )
        }
        break

      case 'ask_request':
        if (msg.question) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'ask',
              content: msg.question!,
              timestamp: new Date(),
              ask: {
                question: msg.question!,
                options: msg.options || [],
                ...(msg.kind ? { kind: msg.kind } : {}),
                ...(msg.app_id ? { app_id: msg.app_id } : {}),
                ...(msg.seed_json ? { seed_json: msg.seed_json } : {}),
              },
            },
          ])
          // Reset accumulator so the (now empty) ask block doesn't bleed into
          // the next assistant message.
          streamAccRef.current = ''
        }
        break

      case 'ai_access_error':
        // Platform allowance exhausted (or no usable key): stop the run and
        // surface the graceful upgrade / add-your-own-key modal instead of a
        // raw error. The run already ended server-side, so no retry hook.
        setStreaming(false)
        import('@/store/aiAccessStore').then(
          ({ reportAiAccessError, pickAiAccessFields }) => {
            // One shared picker: the refusal carries more than a plan and a
            // token pair (which cap tripped, a wallet balance), and copying
            // fields by hand here is what silently dropped them.
            const last = lastTaskRef.current
            reportAiAccessError({
              ...pickAiAccessFields(msg as unknown as Record<string, unknown>),
              resend: last
                ? { project: projectRef.current, kind: 'task', ...last }
                : null,
            })
          })
        break

      case 'error':
        setError(msg.message || 'Unknown error')
        break

      // Another device on this session (the twasta Build phone app, another
      // tab) sent something. Our own echoes carry our client_id.
      case 'user_message': {
        const ev = msg as unknown as { content?: string; client_id?: string; internal?: boolean; attachments?: string[] }
        if (ev.client_id && ev.client_id === clientIdRef.current) break
        const text = ev.content || (ev.attachments?.length ? `📎 ${ev.attachments.length} attachment(s)` : '')
        if (ev.internal) {
          // An answer to the agent's question, given elsewhere: retire the ask.
          setMessages((prev) => {
            const updated = [...prev]
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].role === 'ask' && !updated[i].askAnswered) {
                updated[i] = { ...updated[i], askAnswered: true, askAnsweredWith: text }
                break
              }
            }
            return updated
          })
        } else if (text) {
          setMessages((prev) => [...answerOpenAsks(prev, text), { role: 'user', content: text, timestamp: new Date() }])
        }
        // The other device started a run here — follow it like our own.
        streamAccRef.current = ''
        setStreaming(true)
        break
      }

      // A permission prompt was answered on another device.
      case 'permission_resolved': {
        const ev = msg as unknown as { permission_id?: string; decision?: 'allow' | 'deny'; expired?: boolean }
        if (!ev.permission_id) break
        setMessages((prev) => prev.map((m) => (
          m.role === 'permission' && m.permissionRequest?.permission_id === ev.permission_id && !m.permissionDecision
            ? { ...m, permissionDecision: ev.decision || 'deny', ...(ev.expired ? { permissionExpired: true } : {}) }
            : m
        )))
        break
      }

      default:
        break
    }
  }, [setStreaming])

  // ---------------------------------------------------------------
  // WebSocket connection
  // ---------------------------------------------------------------
  const connect = useCallback((sid: string) => {
    if (fatalErrorRef.current) return

    lastSeqRef.current = afterSeqFor(connectedSidRef.current, sid, lastSeqRef.current)
    connectedSidRef.current = sid
    const wsUrl = `${WS_BASE()}/api/claudeagent/ws/${sid}`
      + (lastSeqRef.current > 0 ? `?after_seq=${lastSeqRef.current}` : '')

    setWsStatus('connecting')

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setWsStatus('connected')
      setError((e) => (e && e.startsWith('Connection to the agent lost') ? null : e))
      reconnectAttemptsRef.current = 0
      authRetryRef.current = false
      // Keep-alive: an open dock is activity, so a session waiting on the
      // user's answer is not retired by the idle TTL while they are away.
      if (pingTimerRef.current) clearInterval(pingTimerRef.current)
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }))
      }, DOCK_PING_MS)
    }

    ws.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string) as WebSocketEvent
        const seq = (data as { seq?: unknown }).seq
        if (typeof seq === 'number') {
          if (seq <= lastSeqRef.current) return
          lastSeqRef.current = seq
        }
        if (data.type === 'error') {
          // Agent-level errors are NOT connection-fatal (the CLI may have
          // hit a transient API error, or the backend refused a duplicate
          // run). Connection-level rejections arrive as 4xxx close codes.
          // Termination of the run is signalled separately via run_state.
          setError(data.message || 'An error occurred')
          // A REFUSED send never started a run: take down the spinner and
          // the empty reply bubble the send put up, or the dock hangs.
          if ((data as { refused?: boolean }).refused && isStreamingRef.current) {
            setStreaming(false)
            setMessages((prev) => unwindRefusedTurn(prev))
          }
          return
        }
        handleMessage(data)
      } catch {
        // non-JSON -- ignore
      }
    }

    ws.onerror = () => {
      setWsStatus('error')
    }

    ws.onclose = (event: CloseEvent) => {
      wsRef.current = null
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }

      if (fatalErrorRef.current) {
        setWsStatus('error')
        return
      }

      // 4401: the login cookie lapsed while the socket was down — refresh it once
      // and reconnect instead of treating the session as gone.
      if (event.code === 4401 && !authRetryRef.current) {
        authRetryRef.current = true
        setWsStatus('disconnected')
        api.post('/auth/refresh')
          .then(() => connect(sid))
          .catch(() => {
            fatalErrorRef.current = true
            setWsStatus('error')
            setError('Your login has expired. Sign in again to continue.')
          })
        return
      }

      // 4004: the session ended (idle too long, or the backend restarted).
      // The conversation is kept here, and the next message joins a fresh
      // session (its plan resumes the CLI conversation) — the dock is NOT
      // dead. A run cannot have outlived its session, so stop its spinner.
      if (event.code === 4004) {
        connectedSidRef.current = null
        lastSeqRef.current = 0
        setSessionId(null)
        setWsStatus('disconnected')
        if (isStreamingRef.current) {
          setStreaming(false)
          setMessages((prev) => [
            ...unwindRefusedTurn(prev),
            {
              role: 'system',
              content: 'The agent session ended while you were away. Send your message again to carry on — nothing is lost.',
              timestamp: new Date(),
            },
          ])
        }
        return
      }

      if (event.code >= 4000 && event.code < 5000) {
        fatalErrorRef.current = true
        setWsStatus('error')
        setError(event.reason || 'Connection rejected by server')
        return
      }

      // Keep reconnecting with a capped backoff — the backend keeps a run
      // alive and buffers its events, and a backend restart or a laptop
      // asleep can outlast any fixed number of attempts. After a few
      // failures say so (and keep trying in the background).
      const attempt = reconnectAttemptsRef.current++
      setWsStatus(attempt >= 5 ? 'error' : 'disconnected')
      if (attempt === 5) {
        setError('Connection to the agent lost — retrying in the background.')
      }
      const delay = Math.min(1000 * 2 ** Math.min(attempt, 4), 16000)
      reconnectTimerRef.current = setTimeout(() => connect(sid), delay)
    }
  }, [handleMessage, setStreaming])

  // ---------------------------------------------------------------
  // Conversation persistence: restore on mount / user- or project-switch,
  // then reattach to the backend session if it is still alive. The backend
  // keeps a detached run alive and buffers its events, so reattaching mid-run
  // replays what was missed and live progress continues (run_state → true).
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!userId) return
    const key = `${userId}:${project}`
    if (restoredKeyRef.current === key) return

    // Detach any socket from the previous user/project without triggering
    // the reconnect loop or an error banner.
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    const oldWs = wsRef.current
    if (oldWs) {
      oldWs.onclose = null
      oldWs.onmessage = null
      oldWs.onerror = null
      wsRef.current = null
      try { oldWs.close() } catch { /* already closed */ }
    }
    reconnectAttemptsRef.current = 0
    fatalErrorRef.current = false
    // A different conversation: its session's seq starts from scratch.
    lastSeqRef.current = 0
    connectedSidRef.current = null
    streamAccRef.current = ''
    setStreaming(false)
    setMessages([])
    setTodos([])
    setChangedFiles([])
    setAgentProgress(null)
    setError(null)
    setSessionId(null)
    setWsStatus('disconnected')

    const persisted = loadAgentChat(PERSIST_ENGINE, userId, project)
    restoredKeyRef.current = key
    if (persisted && persisted.messages.length > 0) {
      const revived = persisted.messages.map((m) => ({
        ...m,
        role: m.role as AgentMessage['role'],
        timestamp: new Date(m.timestamp),
      })) as AgentMessage[]
      const revivedTodos = (persisted.todos || []) as ClaudeTodo[]
      setMessages(revived)
      setTodos(revivedTodos)
      // Seed the flush mirror immediately — an unmount before React commits
      // the restored state must not overwrite the stored transcript with
      // empty state.
      stateRef.current = { messages: revived, todos: revivedTodos, sessionId: persisted.sessionId }
      // Seed the stream accumulator so replayed assistant deltas continue the
      // restored trailing assistant message instead of restarting it.
      const last = revived[revived.length - 1]
      streamAccRef.current = last && last.role === 'assistant' ? last.content : ''
    }

    let stale = false
    ;(async () => {
      // 'alive' / 'dead' are definitive backend answers; 'unknown' means the
      // probe failed (unreachable / older backend) — never treat as dead.
      const probe = async (sid: string): Promise<'alive' | 'dead' | 'unknown'> => {
        try {
          const st = await getAgentSessionState(sid)
          if (typeof st?.exists === 'boolean') return st.exists ? 'alive' : 'dead'
        } catch { /* unreachable */ }
        return 'unknown'
      }
      const attach = (sid: string) => {
        if (stale) return
        setSessionId(sid)
        connect(sid)
      }

      const storedSid = persisted?.sessionId || null
      if (storedSid) {
        const st = await probe(storedSid)
        if (stale) return
        if (st === 'alive') {
          attach(storedSid)
          return
        }
        if (st === 'unknown') return // keep the stored id; retry next load
        // Definitively expired: keep the transcript (the next message
        // re-sends chat history in the prompt) but drop the id so a fresh
        // backend session is created on demand.
        if (persisted) {
          saveAgentChat(PERSIST_ENGINE, userId, project, {
            sessionId: null,
            messages: persisted.messages,
            todos: persisted.todos,
          })
        }
      }

      // No local history (a new browser, cleared storage, or a conversation
      // held on the phone): load it from the server and join its live session.
      if (!persisted || persisted.messages.length === 0) {
        try {
          const { transcriptToMessages } = await import('@/lib/agentTranscript')
          const { data } = await api.get('/claudeagent/transcript', { params: { project, limit: 300 } })
          if (stale) return
          // Never yields 'permission', so it fits either engine's message type.
          const restored = transcriptToMessages(data?.entries || []) as unknown as AgentMessage[]
          if (restored.length) {
            setMessages(restored)
            stateRef.current = { ...stateRef.current, messages: restored }
          }
          const liveSid = data?.session_id as string | null
          if (liveSid && (await probe(liveSid)) === 'alive') {
            if (stale) return
            // The transcript covers the conversation so far; replay the rest.
            lastSeqRef.current = Number(data?.last_seq) || 0
            connectedSidRef.current = liveSid
            attach(liveSid)
            return
          }
        } catch {
          // History is a convenience — the dock still works without it.
        }
      }

      // Fallback: no usable stored session — if a plan is still RUNNING for
      // this project+backend, reattach via its resume_ref session id. Covers
      // conversations started before persistence existed, cleared storage,
      // and other-browser refreshes; the reconnect sync then replays the
      // run's buffered events and restores live progress.
      try {
        const { listPlans } = await import('@/api/agentplans')
        const plans = await listPlans(project)
        if (stale) return
        const running = plans.find(
          (p) => p.backend === 'claude' && p.status === 'running',
        )
        const planSid = (running?.resume_ref as { session_id?: string } | undefined)
          ?.session_id
        if (planSid && planSid !== storedSid && (await probe(planSid)) === 'alive') {
          attach(planSid)
        }
      } catch {
        // Plans REST is optional — without it the dock just stays idle.
      }
    })()
    return () => { stale = true }
  }, [userId, project, connect, setStreaming])

  // Throttled write-through of the conversation to localStorage.
  useEffect(() => {
    if (!userId || restoredKeyRef.current !== `${userId}:${project}`) return
    if (persistTimerRef.current) return
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null
      const s = stateRef.current
      saveAgentChat(PERSIST_ENGINE, userId, project, {
        sessionId: s.sessionId,
        messages: s.messages.map(toPersistedMessage),
        todos: s.todos,
      })
    }, 300)
  }, [messages, todos, sessionId, userId, project])

  // Final flush on refresh/close/unmount so the last streamed deltas survive.
  useEffect(() => {
    if (!userId) return
    const flush = () => {
      if (restoredKeyRef.current !== `${userId}:${project}`) return
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
      const s = stateRef.current
      saveAgentChat(PERSIST_ENGINE, userId, project, {
        sessionId: s.sessionId,
        messages: s.messages.map(toPersistedMessage),
        todos: s.todos,
      })
    }
    window.addEventListener('beforeunload', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      flush()
    }
  }, [userId, project])

  // ---------------------------------------------------------------
  // Create session and connect on demand
  // ---------------------------------------------------------------
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (sessionId && wsRef.current?.readyState === WebSocket.OPEN) {
      return sessionId
    }

    try {
      setError(null)
      fatalErrorRef.current = false
      const resp = await createAgentSession({ project })
      const sid = resp.session_id
      setSessionId(sid)
      connect(sid)
      // Wait briefly for connection
      await new Promise<void>((resolve) => setTimeout(resolve, 500))
      return sid
    } catch (err: unknown) {
      let msg = 'Failed to create agent session'
      const axiosErr = err as { response?: { data?: { detail?: string }; status?: number } }
      if (axiosErr?.response?.data?.detail) {
        msg = axiosErr.response.data.detail
      } else if (axiosErr?.response?.status === 503) {
        msg = 'Claude Code CLI is not installed. Install with: npm install -g @anthropic-ai/claude-code'
      }
      setError(msg)
      return null
    }
  }, [sessionId, project, connect])

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  const startTask = useCallback(async (
    taskType: string,
    taskData: Record<string, unknown>,
  ) => {
    const sid = await ensureSession()
    if (!sid) return
    lastTaskRef.current = { taskType, taskData }

    // Add user message to chat
    const description = (taskData.description || taskData.content || taskType) as string
    setMessages((prev) => [
      ...answerOpenAsks(prev, description),
      { role: 'user', content: description, timestamp: new Date() },
    ])

    streamAccRef.current = ''
    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '', timestamp: new Date() },
    ])
    setStreaming(true)
    setError(null)
    setChangedFiles([])
    setTodos([])

    // Wait a moment for WS to be ready if just created
    const waitForWs = () => new Promise<void>((resolve) => {
      const check = () => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          resolve()
        } else {
          setTimeout(check, 100)
        }
      }
      check()
      setTimeout(resolve, 3000) // timeout after 3s
    })
    await waitForWs()

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        task_type: taskType,
        task_data: taskData,
        client_id: clientIdRef.current,
        device: 'Browser',
      }))
    } else {
      setError('WebSocket not connected')
      setStreaming(false)
    }
  }, [ensureSession, setStreaming])

  // Carry on a turn refused for credits once the user has fixed it.
  useResendAfterAccess({ project, userId, startTask, setMessages })

  // Resume a paused plan: continue the prior CLI conversation via --resume,
  // re-driven by the plan's originating prompt + a "continue" instruction.
  const resumePlan = useCallback(async (planId: string) => {
    const sid = await ensureSession()
    if (!sid) return
    let plan: import('@/api/agentplans').AgentPlan | null = null
    try {
      const { getPlan } = await import('@/api/agentplans')
      plan = await getPlan(project, planId)
    } catch { /* plan detail optional */ }
    const prompt = plan?.originating_prompt || plan?.description || 'your previous plan'
    const claudeSid = (plan?.resume_ref as { claude_session_id?: string } | undefined)?.claude_session_id

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `Resume: ${plan?.title || 'plan'}`, timestamp: new Date() },
    ])
    streamAccRef.current = ''
    setMessages((prev) => [...prev, { role: 'assistant', content: '', timestamp: new Date() }])
    setStreaming(true)
    setError(null)
    setChangedFiles([])
    setTodos([])

    await new Promise<void>((resolve) => {
      const check = () => {
        if (wsRef.current?.readyState === WebSocket.OPEN) resolve()
        else setTimeout(check, 100)
      }
      check()
      setTimeout(resolve, 3000)
    })
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        task_type: 'project_modify',
        task_data: { content: `Continue your plan: ${prompt}. Pick up where you left off.` },
        plan_id: planId,
        ...(claudeSid ? { resume_claude_session_id: claudeSid } : {}),
      }))
    } else {
      setError('WebSocket not connected')
      setStreaming(false)
    }
  }, [ensureSession, project, setStreaming])

  const respondToPermission = useCallback((permissionId: string, decision: 'allow' | 'deny') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'permission_response',
        permission_id: permissionId,
        decision,
      }))
    }
    // Update the message to reflect the decision
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.role === 'permission' && msg.permissionRequest?.permission_id === permissionId) {
          return { ...msg, permissionDecision: decision }
        }
        return msg
      })
    )
    // Restore progress status
    setAgentProgress((prev) =>
      prev ? { ...prev, status: 'thinking' } : prev
    )
  }, [])

  const cancelTask = useCallback(() => {
    if (!isStreaming) return
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }))
    }
  }, [isStreaming])

  // Mark the most recent unanswered clarifying question as answered. The dock
  // sends the answer as the next message (which carries the asked question in
  // chat_history), so this only updates the bubble's display.
  const resolveAsk = useCallback((answer: string) => {
    setMessages((prev) => {
      const updated = [...prev]
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].role === 'ask' && !updated[i].askAnswered) {
          updated[i] = { ...updated[i], askAnswered: true, askAnsweredWith: answer }
          break
        }
      }
      return updated
    })
  }, [])

  const clearMessages = useCallback(() => {
    if (isStreaming) return
    setMessages([])
    setChangedFiles([])
    setError(null)
    setAgentProgress(null)
    setTodos([])
    streamAccRef.current = ''
    if (userId) clearAgentChat(PERSIST_ENGINE, userId, project)
  }, [isStreaming, userId, project])

  // ---------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
      }
      if (pingTimerRef.current) clearInterval(pingTimerRef.current)
      fatalErrorRef.current = true
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      // Allow a remount (e.g. dev StrictMode) to restore + reattach again.
      restoredKeyRef.current = null
    }
  }, [])

  return {
    sessionId,
    messages,
    isStreaming,
    wsStatus,
    error,
    agentProgress,
    changedFiles,
    todos,
    startTask,
    resumePlan,
    cancelTask,
    clearMessages,
    respondToPermission,
    resolveAsk,
  }
}
